// Schema de la unidad atómica del MVP: 1 hora × 1 línea × 1 SKU (ADR-0012).
// Mirror de la tabla `registros_produccion_hora` (migración 00006) + paros
// embebidos.
//
// Validaciones replicadas desde los CHECK del DDL:
//   - cantidad_procesada >= 0 (mig 25 — 0 requiere paro total, refine abajo)
//   - numero_operarios entre 1 y 20
//   - golpes_por_minuto > 0 (nullable, "No aplica" = null)
//   - hora_fin_bloque > hora_inicio_bloque (o hora_fin = '00:00' fin de día)
//   - cantidad_kg >= 0 (nullable)
//   - razon_fuera_turno length >= 20 si es_captura_fuera_turno (mig 00011)
//   - razon_delegacion length >= 20 si capturado_por_supervisor (mig 00011)
//
// El backend hace una validación ADICIONAL antes del INSERT que NO está en el
// schema: verificar existencia en `programacion_lideres` para (user, fecha,
// turno, linea). Si no existe → 403.
import { z } from 'zod';
import { paroCreateSchema, paroSchema } from './paro.js';
import { loteCampoSchema } from './formato_lote.js';
const horaTimeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
// OP de Alpina: exactamente 8 dígitos numéricos (solicitud Robotpack 2026-06-24,
// ej. 10349723). Lo usan los schemas del número de OP de `programacion_op` y de
// facturación. El LOTE de captura (`op_lote_oc_cliente`) ya no lo usa: su
// formato es configurable por empresa (B7, ver `formato_lote.ts`); el default
// de la empresa coincide con esta regla.
export const opLoteOcRegex = /^\d{8}$/;
export const opLoteOcMensaje = 'OP/Lote/OC debe ser exactamente 8 dígitos numéricos';
/**
 * Minutos del bloque cubiertos por los paros del payload, y duración del
 * bloque. Convenciones:
 *   - Los timestamps de paro vienen compuestos como `fecha` + HH:MM en "fake
 *     UTC" (combinarFechaHora del FE) — los bloques se componen igual acá,
 *     así la comparación es manzanas con manzanas.
 *   - Paro ABIERTO (hora_fin=null) cuenta desde su inicio hasta el fin del
 *     bloque (sigue parado al cerrar el bloque).
 *   - Intervalos recortados al bloque y fusionados (los cerrados no se
 *     solapan por validación previa; un abierto sí podría solapar uno
 *     cerrado y no debe contar doble).
 *   - Bloque 23:00–00:00: hora_fin_bloque <= hora_inicio → fin + 1 día.
 *
 * Compartido FE↔BE: el FE lo usa para avisar ANTES de enviar y el schema lo
 * usa en el refine "producción 0 requiere paro total".
 */
export function coberturaParosBloque(r) {
    const [y, m, d] = r.fecha.split('-').map(Number);
    if (!y || !m || !d)
        return { minutosCubiertos: 0, minutosBloque: 60 };
    const toMs = (hora, plusDias = 0) => {
        const [hh, mm, ss = 0] = hora.split(':').map(Number);
        return Date.UTC(y, m - 1, d + plusDias, hh ?? 0, mm ?? 0, ss ?? 0);
    };
    const bloqueIni = toMs(r.hora_inicio_bloque);
    let bloqueFin = toMs(r.hora_fin_bloque);
    if (bloqueFin <= bloqueIni)
        bloqueFin = toMs(r.hora_fin_bloque, 1);
    const DIA_MS = 24 * 60 * 60_000;
    const intervalos = r.paros
        .map((p) => {
        let pIni = Date.parse(p.hora_inicio);
        let pFin = p.hora_fin === null ? Number.NaN : Date.parse(p.hora_fin);
        // Alineación de marco: los paros de la cola post-medianoche de un T3 se
        // componen con fecha del día calendario REAL (combinarFechaHoraTurno),
        // que es fecha operativa + 1 — mientras que el bloque acá se compone con
        // la fecha operativa a secas. Si el paro cae ≥24h después del inicio del
        // bloque, está en ese marco corrido → traerlo de vuelta para comparar.
        if (Number.isFinite(pIni) && pIni - bloqueIni >= DIA_MS) {
            pIni -= DIA_MS;
            if (Number.isFinite(pFin))
                pFin -= DIA_MS;
        }
        return {
            ini: Math.max(pIni, bloqueIni),
            fin: Math.min(p.hora_fin === null ? bloqueFin : pFin, bloqueFin),
        };
    })
        .filter((iv) => Number.isFinite(iv.ini) && Number.isFinite(iv.fin) && iv.fin > iv.ini)
        .sort((a, b) => a.ini - b.ini);
    let cubiertoMs = 0;
    let cursor = bloqueIni;
    for (const iv of intervalos) {
        const desde = Math.max(iv.ini, cursor);
        if (iv.fin > desde) {
            cubiertoMs += iv.fin - desde;
            cursor = iv.fin;
        }
    }
    return {
        minutosCubiertos: cubiertoMs / 60_000,
        minutosBloque: (bloqueFin - bloqueIni) / 60_000,
    };
}
/** Estado de un registro: válido o invalidado (soft delete, ADR-0020). */
export const estadoRegistroSchema = z.enum(['valido', 'invalidado']);
/** Origen del bloque que se está capturando: viene del programa o es extra. */
export const origenSolicitudSchema = z.enum(['programa', 'pedido_extra', 'rework']);
/**
 * Payload para crear un registro desde L2. Lo que el frontend manda al POST.
 * El backend deriva planta_id, area_id, cliente_id desde linea_id + sku_id
 * (no se mandan explícitos — la BD trigger los valida igual).
 */
export const registroProduccionHoraCreateSchema = z
    .object({
    // Identificación temporal
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    hora_inicio_bloque: z.string().regex(horaTimeRegex, 'Formato HH:MM'),
    hora_fin_bloque: z.string().regex(horaTimeRegex, 'Formato HH:MM'),
    // Qué se produjo
    linea_id: z.string().uuid(),
    sku_id: z.string().uuid(),
    // Captura. 0 es VÁLIDO solo si los paros cubren el bloque completo
    // (refine abajo) — una hora con la línea parada se registra con 0 + paro
    // total, no con un 1 simbólico (decisión 2026-06-10).
    cantidad_procesada: z.number().min(0, 'No puede ser negativa'),
    cantidad_kg: z.number().min(0).nullable().default(null),
    golpes_por_minuto: z.number().positive('Debe ser mayor a 0').nullable().default(null),
    numero_operarios: z.number().int().min(1, 'Mínimo 1 operario').max(20, 'Máximo 20 operarios'),
    op_lote_oc_cliente: loteCampoSchema,
    // Flags de contexto
    es_captura_fuera_turno: z.boolean().default(false),
    razon_fuera_turno: z.string().trim().max(500).nullable().default(null),
    capturado_por_supervisor: z.boolean().default(false),
    razon_delegacion: z.string().trim().max(500).nullable().default(null),
    // Si capturado_por_supervisor=true, este es el líder a quien se le delega
    lider_asignado_user_id: z.string().uuid().nullable().default(null),
    // Programación
    origen_solicitud: origenSolicitudSchema.default('programa'),
    programado: z.boolean().default(true),
    // Paros embebidos (0..N)
    paros: z.array(paroCreateSchema).default([]),
})
    .refine((r) => finSegundosTramo(r.hora_fin_bloque) > toSegundosTramo(r.hora_inicio_bloque), {
    message: 'hora_fin_bloque debe ser posterior a hora_inicio_bloque (o "00:00" fin de día)',
    path: ['hora_fin_bloque'],
})
    .refine((r) => !r.es_captura_fuera_turno ||
    (r.razon_fuera_turno !== null && r.razon_fuera_turno.length >= 20), {
    message: 'Razón fuera de turno obligatoria (mínimo 20 caracteres)',
    path: ['razon_fuera_turno'],
})
    .refine((r) => !r.capturado_por_supervisor ||
    (r.razon_delegacion !== null && r.razon_delegacion.length >= 20), {
    message: 'Razón de delegación obligatoria (mínimo 20 caracteres)',
    path: ['razon_delegacion'],
})
    .refine((r) => !r.capturado_por_supervisor || r.lider_asignado_user_id !== null, {
    message: 'lider_asignado_user_id requerido cuando capturado_por_supervisor',
    path: ['lider_asignado_user_id'],
})
    // Paros: la suma de duración de paros CERRADOS no puede exceder la duración
    // REAL del bloque (no un hardcode de 60 min: un bloque de borde parcial,
    // ej. Sopó T2 13:30–14:00, dura 30'). `coberturaParosBloque` ya calcula esa
    // duración (con wrap de medianoche) — se reusa, no se triplica el cálculo.
    // Los abiertos (hora_fin=null) no se cuentan acá — su duración se determina
    // al cerrar. Esto evita capturas físicamente imposibles como 3 paros de
    // 30 min dentro de un bloque de 1 hora (o de 30' en un parcial).
    .refine((r) => {
    const totalMin = r.paros.reduce((acc, p) => {
        if (p.hora_fin === null)
            return acc;
        const inicio = new Date(p.hora_inicio).getTime();
        const fin = new Date(p.hora_fin).getTime();
        return acc + (fin - inicio) / 60_000;
    }, 0);
    return totalMin <= coberturaParosBloque(r).minutosBloque;
}, (r) => ({
    message: `La suma de duración de paros no puede exceder ${coberturaParosBloque(r).minutosBloque} min (duración del bloque)`,
    path: ['paros'],
}))
    // Paros: no pueden solaparse entre sí en el mismo bloque. Solo se valida sobre
    // paros con hora_fin definida; los abiertos no se chequean (no se sabe rango).
    .refine((r) => {
    const cerrados = r.paros
        .filter((p) => p.hora_fin !== null)
        .map((p) => ({
        inicio: new Date(p.hora_inicio).getTime(),
        fin: new Date(p.hora_fin).getTime(),
    }))
        .sort((a, b) => a.inicio - b.inicio);
    for (let i = 1; i < cerrados.length; i++) {
        const prev = cerrados[i - 1];
        const cur = cerrados[i];
        if (cur.inicio < prev.fin)
            return false;
    }
    return true;
}, {
    message: 'Hay paros que se solapan en el tiempo',
    path: ['paros'],
})
    // Producción 0: solo válida si los paros declarados cubren TODO el bloque
    // (paro total de la hora). Evita ceros "de dedo" sin respaldo y mantiene la
    // semántica: 0 unidades ⇔ la línea no corrió en ningún minuto del bloque.
    .refine((r) => {
    if (r.cantidad_procesada !== 0)
        return true;
    if (r.paros.length === 0)
        return false;
    const { minutosCubiertos, minutosBloque } = coberturaParosBloque(r);
    return minutosCubiertos >= minutosBloque - 0.01;
}, {
    message: 'Producción 0 requiere paros que cubran el bloque completo (la hora entera parada)',
    path: ['cantidad_procesada'],
})
    // Producción 0 ⇒ la máquina no corrió ⇒ golpes/min no tiene sentido.
    .refine((r) => r.cantidad_procesada !== 0 || r.golpes_por_minuto === null, {
    message: 'Golpes/min debe ir vacío cuando la producción es 0',
    path: ['golpes_por_minuto'],
});
// ===========================================================================
// Captura por tramos SKU (2+ SKUs por hora, rango real por tramo).
// Change `multi-sku-por-hora`.
// ===========================================================================
//
// Un bloque contenedor con N `tramos[]` que particionan sin huecos ni solapes
// el rango horario capturado. El caso mono-SKU de siempre es un bloque de 1
// tramo que cubre toda la celda — `liftLegacy` normaliza el payload plano
// legacy a esta forma ANTES de validar, preservando un solo camino de
// validación/inserción (Decisión 1 del design: normalizador, NO
// discriminated union).
/**
 * Segundos desde 00:00 de un HH:MM o HH:MM:SS.
 * Es la unidad canónica para no ocultar huecos/solapes subminuto.
 */
export function toSegundosTramo(hora) {
    const [hh, mm, ss = 0] = hora.split(':').map(Number);
    return (hh ?? 0) * 3600 + (mm ?? 0) * 60 + (ss ?? 0);
}
/** Segundos de FIN linealizados; solo medianoche exacta representa 24:00. */
export function finSegundosTramo(hora) {
    const segundos = toSegundosTramo(hora);
    return segundos === 0 ? 24 * 3600 : segundos;
}
/** Representación comparable, siempre HH:MM:SS. */
function normalizarHoraTramo(hora) {
    return hora.length === 5 ? `${hora}:00` : hora;
}
/**
 * Minutos desde 00:00. Puede devolver fracción cuando el contrato incluye
 * segundos; se conserva por compatibilidad con consumidores FE.
 * Exportada: el FE (Lote 4, `BarraTramos.tsx`) la reusa para validación en
 * vivo del modal "Agregar tramo" sin duplicar la aritmética.
 */
export function toMinutosTramo(hhmm) {
    return toSegundosTramo(hhmm) / 60;
}
/**
 * Minutos de FIN linealizados: "00:00" siempre es fin de día (1440), nunca 0.
 * Exportada por el mismo motivo que `toMinutosTramo`.
 */
export function finMinutosTramo(hhmm) {
    return finSegundosTramo(hhmm) / 60;
}
/**
 * Ordena tramos por inicio real, linealizando el caso post-medianoche: un
 * tramo que arranca en "00:00" solo se considera "después" si ALGÚN otro
 * tramo del mismo bloque termina en "00:00" (wrap genuino, ej. T3
 * 23:45→00:00 + 00:00→00:15 — ver adversarial de Lote 3). Sin ese wrap,
 * "00:00" es un inicio de día normal y ordena primero.
 */
function ordenarTramosPorInicio(tramos) {
    const hayWrap = tramos.some((t) => toSegundosTramo(t.hora_fin_bloque) === 0);
    const clave = (t) => {
        const ini = toMinutosTramo(t.hora_inicio_bloque);
        if (hayWrap && ini === 0 && toSegundosTramo(t.hora_fin_bloque) !== 0)
            return 24 * 60;
        return ini;
    };
    return [...tramos].sort((a, b) => clave(a) - clave(b));
}
/**
 * Rango total [desde, hasta) del bloque a partir de sus tramos ordenados.
 * Exportada: el handler BE (Lote 3) la reusa para reportar `celda` en la
 * respuesta del bloque — NO para el gate de vigencia (ese valida CADA tramo
 * contra su propia celda real del turno, ver `registros.ts`, porque un bloque
 * puede legítimamente cruzar de una celda a la adyacente, ej. T3
 * post-medianoche 23:45→00:00 + 00:00→00:15).
 */
export function rangoTotalTramos(tramos) {
    const ordenados = ordenarTramosPorInicio(tramos);
    return {
        desde: ordenados[0].hora_inicio_bloque,
        hasta: ordenados[ordenados.length - 1].hora_fin_bloque,
    };
}
/** Un tramo individual: rango real dentro de una celda + SKU + captura. */
export const registroTramoCreateSchema = z
    .object({
    // desde/hasta REALES del tramo (no necesariamente el borde de la celda).
    hora_inicio_bloque: z.string().regex(horaTimeRegex, 'Formato HH:MM'),
    hora_fin_bloque: z.string().regex(horaTimeRegex, 'Formato HH:MM'),
    sku_id: z.string().uuid(),
    cantidad_procesada: z.number().min(0, 'No puede ser negativa'),
    cantidad_kg: z.number().min(0).nullable().default(null),
    golpes_por_minuto: z.number().positive('Debe ser mayor a 0').nullable().default(null),
    numero_operarios: z.number().int().min(1, 'Mínimo 1 operario').max(20, 'Máximo 20 operarios'),
    op_lote_oc_cliente: loteCampoSchema,
})
    .refine((r) => finSegundosTramo(r.hora_fin_bloque) > toSegundosTramo(r.hora_inicio_bloque), {
    message: 'hasta debe ser posterior a desde (o "00:00" fin de día)',
    path: ['hora_fin_bloque'],
})
    // Producción 0 ⇒ la máquina no corrió ⇒ golpes/min no tiene sentido. Movido
    // de "bloque" (schema legacy) a "tramo" — cada tramo es su propia unidad.
    .refine((r) => r.cantidad_procesada !== 0 || r.golpes_por_minuto === null, {
    message: 'Golpes/min debe ir vacío cuando la producción es 0',
    path: ['golpes_por_minuto'],
});
/**
 * Bloque contenedor con `tramos[]` (mínimo 1). Retrocompat: `liftLegacy`
 * eleva el payload plano mono-SKU actual a esta forma antes de validar — un
 * solo schema, un solo camino de validación/inserción (Decisión 1, design
 * `multi-sku-por-hora`).
 */
export const registroBloqueCreateSchema = z
    .object({
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    linea_id: z.string().uuid(),
    // Nivel bloque: contexto y paros (decisión de propuesta).
    es_captura_fuera_turno: z.boolean().default(false),
    razon_fuera_turno: z.string().trim().max(500).nullable().default(null),
    capturado_por_supervisor: z.boolean().default(false),
    razon_delegacion: z.string().trim().max(500).nullable().default(null),
    lider_asignado_user_id: z.string().uuid().nullable().default(null),
    origen_solicitud: origenSolicitudSchema.default('programa'),
    programado: z.boolean().default(true),
    // A nivel bloque; se clipean por tramo al insertar (BE, Lote 3).
    paros: z.array(paroCreateSchema).default([]),
    tramos: z.array(registroTramoCreateSchema).min(1, 'Mínimo 1 tramo'),
})
    // 1. Partición sin huecos ni solapes: ordenados por inicio real, el fin de
    //    cada tramo debe coincidir EXACTO con el inicio del siguiente.
    .refine((r) => {
    if (r.tramos.length === 0)
        return true; // ya lo reporta el .min(1) de arriba
    const ordenados = ordenarTramosPorInicio(r.tramos);
    for (let i = 0; i < ordenados.length - 1; i++) {
        if (normalizarHoraTramo(ordenados[i].hora_fin_bloque) !==
            normalizarHoraTramo(ordenados[i + 1].hora_inicio_bloque)) {
            return false;
        }
    }
    return true;
}, {
    message: 'Los tramos deben particionar el rango sin huecos ni solapes',
    path: ['tramos'],
})
    // 2. Cada tramo, individualmente, no cruza un borde de hora (mismo
    //    invariante que el trigger D10 "sub-bloques por corte de SKU" — se
    //    valida acá para dar 400 claro antes que el P0001 de la BD).
    .refine((r) => r.tramos.every((t) => {
    const horaIni = Math.floor(toSegundosTramo(t.hora_inicio_bloque) / 3600);
    const horaFin = Math.floor((finSegundosTramo(t.hora_fin_bloque) - 1) / 3600);
    return horaIni === horaFin;
}), {
    message: 'Cada tramo debe caer dentro de una sola celda horaria',
    path: ['tramos'],
})
    // 3. Paros (a nivel bloque) no exceden la duración TOTAL real del bloque
    //    (rango [min desde, max hasta) de los tramos — no un hardcode de 60;
    //    un bloque parcial de borde, ej. Sopó 13:30–14:00, dura 30').
    .refine((r) => {
    if (r.tramos.length === 0)
        return true;
    const { desde, hasta } = rangoTotalTramos(r.tramos);
    const { minutosBloque } = coberturaParosBloque({
        fecha: r.fecha,
        hora_inicio_bloque: desde,
        hora_fin_bloque: hasta,
        paros: r.paros,
    });
    const totalParosMin = r.paros.reduce((acc, p) => {
        if (p.hora_fin === null)
            return acc;
        return acc + (Date.parse(p.hora_fin) - Date.parse(p.hora_inicio)) / 60_000;
    }, 0);
    return totalParosMin <= minutosBloque + 0.01;
}, (r) => {
    const { desde, hasta } = rangoTotalTramos(r.tramos);
    const { minutosBloque } = coberturaParosBloque({
        fecha: r.fecha,
        hora_inicio_bloque: desde,
        hora_fin_bloque: hasta,
        paros: r.paros,
    });
    return {
        message: `La suma de duración de paros no puede exceder ${minutosBloque} min (duración del bloque)`,
        path: ['paros'],
    };
})
    // 4. Producción 0 en un tramo exige que los paros del bloque, CLIPEADOS al
    //    rango de ESE tramo, cubran su duración completa (traslada el refine
    //    de "bloque" a "tramo" — no existe tramo fantasma sin cobertura).
    .superRefine((r, ctx) => {
    r.tramos.forEach((t, i) => {
        if (t.cantidad_procesada !== 0)
            return;
        const { minutosCubiertos, minutosBloque } = coberturaParosBloque({
            fecha: r.fecha,
            hora_inicio_bloque: t.hora_inicio_bloque,
            hora_fin_bloque: t.hora_fin_bloque,
            paros: r.paros,
        });
        if (minutosCubiertos < minutosBloque - 0.01) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Tramo ${i}: producción 0 requiere paros que cubran su duración completa`,
                path: ['tramos', i, 'cantidad_procesada'],
            });
        }
    });
})
    // 5. Paros (a nivel bloque) no pueden solaparse entre sí. Paridad con el
    //    schema legacy (`registroProduccionHoraCreateSchema`), que sí tenía
    //    este refine explícito — quedó pendiente en Lote 1 (ver apply-progress,
    //    "Desviaciones") y se cierra acá para no debilitar el no-solapamiento
    //    de paros que ya validaban los tests existentes de `registros-create`.
    //    Solo se valida sobre paros con `hora_fin` definida; los abiertos no se
    //    chequean (no se sabe rango).
    .refine((r) => {
    const cerrados = r.paros
        .filter((p) => p.hora_fin !== null)
        .map((p) => ({
        inicio: new Date(p.hora_inicio).getTime(),
        fin: new Date(p.hora_fin).getTime(),
    }))
        .sort((a, b) => a.inicio - b.inicio);
    for (let i = 1; i < cerrados.length; i++) {
        const prev = cerrados[i - 1];
        const cur = cerrados[i];
        if (cur.inicio < prev.fin)
            return false;
    }
    return true;
}, {
    message: 'Hay paros que se solapan en el tiempo',
    path: ['paros'],
})
    .refine((r) => !r.es_captura_fuera_turno ||
    (r.razon_fuera_turno !== null && r.razon_fuera_turno.length >= 20), {
    message: 'Razón fuera de turno obligatoria (mínimo 20 caracteres)',
    path: ['razon_fuera_turno'],
})
    .refine((r) => !r.capturado_por_supervisor ||
    (r.razon_delegacion !== null && r.razon_delegacion.length >= 20), {
    message: 'Razón de delegación obligatoria (mínimo 20 caracteres)',
    path: ['razon_delegacion'],
})
    .refine((r) => !r.capturado_por_supervisor || r.lider_asignado_user_id !== null, {
    message: 'lider_asignado_user_id requerido cuando capturado_por_supervisor',
    path: ['lider_asignado_user_id'],
});
/**
 * Normalizador de retrocompat (Decisión 1, design `multi-sku-por-hora`): toma
 * el payload plano mono-SKU legacy (lo que la PWA vieja sigue mandando) y lo
 * eleva a un bloque de 1 tramo equivalente, ANTES de parsear con
 * `registroBloqueCreateSchema`. El handler decide con `'tramos' in req.body`
 * — si ya viene en forma de bloque, `liftLegacy` NO se llama.
 */
export function liftLegacy(payload) {
    if (payload === null || typeof payload !== 'object')
        return payload;
    const p = payload;
    const { hora_inicio_bloque, hora_fin_bloque, sku_id, cantidad_procesada, cantidad_kg, golpes_por_minuto, numero_operarios, op_lote_oc_cliente, ...bloqueFields } = p;
    return {
        ...bloqueFields,
        tramos: [
            {
                hora_inicio_bloque,
                hora_fin_bloque,
                sku_id,
                cantidad_procesada,
                cantidad_kg,
                golpes_por_minuto,
                numero_operarios,
                op_lote_oc_cliente,
            },
        ],
    };
}
/**
 * Clipea los paros de un bloque (a nivel bloque, ver `registroBloqueCreateSchema`)
 * al rango REAL de UN tramo — usado por el INSERT transaccional (Lote 3, BE):
 * un paro puede solapar 2+ tramos y genera una fila `registros_paro` por cada
 * tramo que cubre, recortada a su rango, con FK a ESE tramo (no al bloque).
 *
 * Reusa la MISMA convención "fake UTC" + alineación de wrap post-medianoche
 * que `coberturaParosBloque` (ver comentario ahí arriba) — idéntico cálculo,
 * ahora devolviendo los INTERVALOS recortados en vez de solo su suma.
 *
 * Paros ABIERTOS (`hora_fin=null`): pertenecen ÚNICAMENTE al tramo que
 * contiene su `hora_inicio`, SIN recortar el fin (se preserva `null`) —
 * partir un intervalo sin fin conocido entre 2 tramos no tiene sentido de
 * negocio (el mono-tramo legacy con paro abierto sigue idéntico a hoy).
 */
export function clipParosATramo(tramo, paros) {
    const [y, m, d] = tramo.fecha.split('-').map(Number);
    if (!y || !m || !d)
        return [];
    const toMs = (hora, plusDias = 0) => {
        const [hh, mm, ss = 0] = hora.split(':').map(Number);
        return Date.UTC(y, m - 1, d + plusDias, hh ?? 0, mm ?? 0, ss ?? 0);
    };
    const tIni = toMs(tramo.hora_inicio_bloque);
    let tFin = toMs(tramo.hora_fin_bloque);
    if (tFin <= tIni)
        tFin = toMs(tramo.hora_fin_bloque, 1);
    const DIA_MS = 24 * 60 * 60_000;
    const out = [];
    for (const p of paros) {
        let pIni = Date.parse(p.hora_inicio);
        let pFin = p.hora_fin === null ? null : Date.parse(p.hora_fin);
        // Misma alineación de marco que coberturaParosBloque: un paro compuesto
        // con la fecha calendario REAL (cola post-medianoche) queda ≥24h después
        // del tramo cuando este NO cruzó — traerlo de vuelta para comparar.
        if (Number.isFinite(pIni) && pIni - tIni >= DIA_MS) {
            pIni -= DIA_MS;
            if (pFin !== null && Number.isFinite(pFin))
                pFin -= DIA_MS;
        }
        if (pFin === null) {
            if (pIni >= tIni && pIni < tFin) {
                out.push({
                    paro_evento_id: p.paro_evento_id,
                    causal_madre_id: p.causal_madre_id,
                    causal_id: p.causal_id,
                    observaciones: p.observaciones,
                    hora_inicio: new Date(pIni).toISOString(),
                    hora_fin: null,
                });
            }
            continue;
        }
        const ini = Math.max(pIni, tIni);
        const fin = Math.min(pFin, tFin);
        if (fin > ini) {
            out.push({
                paro_evento_id: p.paro_evento_id,
                causal_madre_id: p.causal_madre_id,
                causal_id: p.causal_id,
                observaciones: p.observaciones,
                hora_inicio: new Date(ini).toISOString(),
                hora_fin: new Date(fin).toISOString(),
            });
        }
    }
    return out;
}
/** Schema completo del registro persistido (lo que devuelve el backend). */
export const registroProduccionHoraSchema = z.object({
    id: z.string().uuid(),
    bloque_captura_id: z.string().uuid().optional(),
    tenant_id: z.string().uuid(),
    fecha: z.string().date(),
    turno_id: z.string().uuid(),
    hora_inicio_bloque: z.string(),
    hora_fin_bloque: z.string(),
    planta_id: z.string().uuid(),
    area_id: z.string().uuid(),
    linea_id: z.string().uuid(),
    sku_id: z.string().uuid(),
    cliente_id: z.string().uuid(),
    cantidad_procesada: z.number(),
    cantidad_kg: z.number().nullable(),
    golpes_por_minuto: z.number().nullable(),
    numero_operarios: z.number().int(),
    op_lote_oc_cliente: z.string().nullable(),
    capturado_por_user_id: z.string().uuid(),
    lider_asignado_user_id: z.string().uuid().nullable(),
    supervisor_user_id: z.string().uuid().nullable(),
    capturado_en_turno_id: z.string().uuid(),
    es_captura_fuera_turno: z.boolean(),
    razon_fuera_turno: z.string().nullable(),
    capturado_por_supervisor: z.boolean(),
    razon_delegacion: z.string().nullable(),
    origen_solicitud: origenSolicitudSchema,
    programado: z.boolean(),
    validado_por_user_id: z.string().uuid().nullable(),
    validado_at: z.string().datetime({ offset: true }).nullable(),
    estado: estadoRegistroSchema,
    // Soft delete (ADR-0020): si estado='invalidado', estos 3 campos son
    // obligatorios. Razón ≥20 chars (constraint mig 00011 bloque E).
    invalidado_at: z.string().datetime({ offset: true }).nullable(),
    invalidado_por_user_id: z.string().uuid().nullable(),
    razon_invalidacion: z.string().nullable(),
    // Estado de edición / desbloqueo (mig 21). NULL = sin solicitud.
    estado_edicion: z.enum(['solicitada', 'desbloqueada']).nullable(),
    edicion_solicitada_por_id: z.string().uuid().nullable(),
    edicion_solicitada_at: z.string().datetime({ offset: true }).nullable(),
    razon_solicitud_edicion: z.string().nullable(),
    edicion_desbloqueada_por_id: z.string().uuid().nullable(),
    edicion_desbloqueada_at: z.string().datetime({ offset: true }).nullable(),
    // Corrección de datos (mig 41). origen_correccion=true ⇒ alta retroactiva (faltó
    // la captura original). corregido_* guarda SOLO la última corrección (historial
    // completo en eventos_auditoria). razon_correccion ≥20 chars cuando hay corrección.
    //
    // Opcionales (no `.default()`): este es el schema de RESPUESTA — usar `.default()`
    // acá divergía `z.input` (boolean|undefined) de `z.output` (boolean) y rompía a
    // todos los consumidores que pasan un `RegistroProduccionHora` por props/useState
    // ("Two different types with this name exist"). Con `.optional()` el tipo inferido
    // es uniforme y los endpoints de lectura que aún no proyectan estas columnas siguen
    // parseando (ausencia ⇒ undefined ⇒ "sin corrección").
    origen_correccion: z.boolean().optional(),
    corregido_por_user_id: z.string().uuid().nullable().optional(),
    corregido_at: z.string().datetime({ offset: true }).nullable().optional(),
    razon_correccion: z.string().nullable().optional(),
    // Capacidad estándar del SKU en esta línea (enriquecido desde skus_lineas).
    // Nullable: el registro puede no tener mapeo skus_lineas. Para el % cumplimiento.
    capacidad_estandar_unidades_hora: z.number().nullable(),
    // ¿El líder dueño puede editar este bloque AHORA? Lo computa el backend en SQL
    // (turno activo + ventana de gracia post-turno) — fuente única de verdad. El FE
    // solo lo lee para mostrar/ocultar el botón Editar. Opcional: el POST no lo
    // computa (un registro recién creado en turno siempre es editable).
    editable_por_lider: z.boolean().optional(),
    // Ingesta de máquina (propuesta 2026-10-alimentos-rionegro). Opcionales por la
    // misma razón que origen_correccion: schema de RESPUESTA, sin `.default()`.
    fuente: z.enum(['manual', 'maquina']).optional(),
    pendiente_completar: z.boolean().optional(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
    // No usamos .default([]) acá porque cambia el tipo inferido (input vs
    // output) y rompe useState<RegistroProduccionHora>. La API SIEMPRE devuelve
    // un array (vacío si no hay paros) — el handler garantiza eso vía json_agg
    // con COALESCE.
    paros: z.array(paroSchema),
});
/**
 * Respuesta de `POST /api/registros` cuando el bloque tiene 2+ tramos (Lote 3,
 * multi-sku-por-hora). `celda` es el rango total cubierto por los tramos
 * (`rangoTotalTramos`); `tramos` es 1 objeto `registroProduccionHoraSchema`
 * por fila insertada (con SUS paros clipeados embebidos). El caso de 1 solo
 * tramo (legacy o bloque nuevo mono-tramo) NO usa este shape — devuelve el
 * objeto plano de siempre (no-regresión). Vive acá (no ad-hoc en
 * `apps/api/src/routes/registros.ts`) para que el FE (Lote 4) la importe sin
 * duplicar el contrato.
 */
export const bloqueRegistradoSchema = z.object({
    celda: z.object({ inicio: z.string(), fin: z.string() }),
    tramos: z.array(registroProduccionHoraSchema),
});
// ===========================================================================
// Edición de registros + flujo de desbloqueo (mig 21).
// ===========================================================================
/**
 * Payload para editar un registro existente (PATCH /:id). Solo campos de
 * contenido (la identidad temporal fecha/turno/hora/línea es inmutable).
 * Todos opcionales pero al menos uno (≠ razon) requerido. El SERVER exige
 * `razon` ≥20 según la rama (supervisor o edición vía desbloqueo); en la
 * ventana libre del líder es ignorada.
 */
export const editarRegistroSchema = z
    .object({
    // 0 permitido (mig 25): el SERVER verifica que los paros YA guardados del
    // registro cubran el bloque completo antes de aceptar la edición a 0.
    cantidad_procesada: z.number().min(0, 'No puede ser negativa').optional(),
    cantidad_kg: z.number().min(0).nullable().optional(),
    golpes_por_minuto: z.number().positive('Debe ser mayor a 0').nullable().optional(),
    numero_operarios: z
        .number()
        .int()
        .min(1, 'Mínimo 1 operario')
        .max(20, 'Máximo 20 operarios')
        .optional(),
    op_lote_oc_cliente: loteCampoSchema.optional(),
    sku_id: z.string().uuid().optional(),
    razon: z.string().trim().min(20, 'Mínimo 20 caracteres').optional(),
})
    .refine((o) => Object.keys(o).some((k) => k !== 'razon'), {
    message: 'Debe modificar al menos un campo',
});
/** Query del historial del líder (rango de fechas + filtros + paginación). */
export const historialRegistrosQuerySchema = z.object({
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    turno_id: z.string().uuid().optional(),
    incluir_invalidados: z.coerce.boolean().default(false),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** Respuesta paginada del historial. */
export const historialRegistrosSchema = z.object({
    items: z.array(registroProduccionHoraSchema),
    total: z.number().int().min(0),
    limit: z.number().int(),
    offset: z.number().int(),
});
//# sourceMappingURL=registro_produccion_hora.js.map